import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { reviewApprovalActions as actions } from '.';
import request from 'utils/request';
import { formUrlParameters } from 'utils/helpers';
import {
  MyApprovalsQueryParams,
  MyHistoryQueryParams,
  MyApprovalRequestsQueryParams
} from '../types';

/**
 * Same per-resource GET shape Home's slice/saga.ts uses (`call(request,
 * url)` -> `put(success)`, `catch` -> `put(failure)` with only serializable
 * error info) - no bespoke fetch/error-handling logic introduced here.
 * `REACT_APP_API_V2_URL` already resolves to `.../product-roadmap/api/v2`
 * (see Home's own `/moments`, `/product-hierarchy-filters` usages), so only
 * `/approvals/...` needs appending, not the full `/api/v2/approvals/...`
 * path documented in ../types.ts's doc comments.
 */
const API_V2_URL = process.env.REACT_APP_API_V2_URL;

function toErrorInfo(e: any, fallbackMessage: string) {
  return {
    status: e?.response?.status || 'unknown',
    statusText: e?.response?.statusText || 'Network Error',
    message: e?.message || fallbackMessage
  };
}

export function* loadMyApprovals(
  action: PayloadAction<MyApprovalsQueryParams>
) {
  try {
    const queryString = formUrlParameters(action.payload as any);
    const response = yield call(
      request,
      `${API_V2_URL}/approvals/my-approvals?${queryString}`
    );
    yield put(actions.myApprovalsLoaded(response));
  } catch (e: any) {
    yield put(
      actions.loadMyApprovalsError(
        toErrorInfo(
          e,
          'An error occurred while loading My Reviews and Approvals'
        )
      )
    );
  }
}

export function* loadMyHistory(action: PayloadAction<MyHistoryQueryParams>) {
  try {
    const queryString = formUrlParameters(action.payload as any);
    const response = yield call(
      request,
      `${API_V2_URL}/approvals/my-history?${queryString}`
    );
    yield put(actions.myHistoryLoaded(response));
  } catch (e: any) {
    yield put(
      actions.loadMyHistoryError(
        toErrorInfo(e, 'An error occurred while loading My History')
      )
    );
  }
}

export function* loadMyApprovalRequests(
  action: PayloadAction<MyApprovalRequestsQueryParams>
) {
  try {
    const queryString = formUrlParameters(action.payload as any);
    const response = yield call(
      request,
      `${API_V2_URL}/approvals/my-reviews?${queryString}`
    );
    yield put(actions.myApprovalRequestsLoaded(response));
  } catch (e: any) {
    yield put(
      actions.loadMyApprovalRequestsError(
        toErrorInfo(e, 'An error occurred while loading My Approval Requests')
      )
    );
  }
}

export function* reviewApprovalSaga() {
  yield takeLatest(actions.loadMyApprovals.type, loadMyApprovals);
  yield takeLatest(actions.loadMyHistory.type, loadMyHistory);
  yield takeLatest(actions.loadMyApprovalRequests.type, loadMyApprovalRequests);
}
